import { useEffect, useState } from 'react';

import type { AgentTodoItem } from './types';

/** Gap between consecutive status flips when the agent lands a batch at once. */
export const TODO_TICK_STAGGER_MS = 160;

const isSamePlan = (
  displayed: readonly AgentTodoItem[],
  incoming: readonly AgentTodoItem[]
): boolean =>
  displayed.length === incoming.length &&
  displayed.every(
    (item, index) =>
      item.id === incoming[index]?.id && item.title === incoming[index]?.title
  );

const firstStatusDiffIndex = (
  displayed: readonly AgentTodoItem[],
  incoming: readonly AgentTodoItem[]
): number =>
  displayed.findIndex((item, index) => item.status !== incoming[index]?.status);

/**
 * The agent can flip several todo statuses in a single tool call; ticking them
 * all in one frame reads as a glitch. Catch the displayed list up one position
 * at a time so a batch strikes through in timeline order.
 */
export const useStaggeredTodoItems = (
  items: readonly AgentTodoItem[]
): readonly AgentTodoItem[] => {
  const [displayed, setDisplayed] = useState<readonly AgentTodoItem[]>(items);

  useEffect(() => {
    if (!isSamePlan(displayed, items)) {
      setDisplayed(items);
      return;
    }

    const index = firstStatusDiffIndex(displayed, items);
    const nextItem = index === -1 ? undefined : items[index];
    if (!nextItem) return;

    const timer = setTimeout(() => {
      setDisplayed((prev) =>
        prev.map((item, position) => (position === index ? nextItem : item))
      );
    }, TODO_TICK_STAGGER_MS);

    return () => clearTimeout(timer);
  }, [items, displayed]);

  return displayed;
};
